import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  getProviderShootout,
  listVideoProviders,
  startProviderShootout,
  rewriteDialogueForPrompt,
} from "../lib/api";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Provider Shootout — animate a single still through every available
 * image-to-video model in parallel, then show the user the three
 * resulting MP4s side-by-side so they can pick the winner.
 *
 * Props:
 *  - open: bool                  — controls visibility
 *  - onClose: () => void
 *  - sourceImageUrl: string?     — pre-selected scene still URL (loaded
 *                                  into the picker); user can override
 *                                  by selecting a different File.
 *  - sourcePrompt: string?       — pre-selected scene prompt
 *  - onPick: (providerId) => void  — fires when user clicks "Use this model"
 */
export default function ProviderShootoutModal({
  open,
  onClose,
  sourceImageUrl = "",
  sourcePrompt = "",
  onPick,
}) {
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState("");
  const [prompt, setPrompt] = useState(sourcePrompt);
  const [busy, setBusy] = useState(false);
  const [testId, setTestId] = useState("");
  const [test, setTest] = useState(null);
  const [providers, setProviders] = useState([]);
  const [rewriting, setRewriting] = useState(false);
  const pollRef = useRef(null);

  // Load catalogue + reset on open.
  useEffect(() => {
    if (!open) return;
    setPrompt(sourcePrompt || "");
    setFile(null);
    setFilePreview("");
    setTestId("");
    setTest(null);
    setBusy(false);
    (async () => {
      try {
        const data = await listVideoProviders();
        setProviders(
          (data?.providers || []).filter((p) => p.available),
        );
      } catch {
        setProviders([]);
      }
    })();
  }, [open, sourcePrompt]);

  // Polling loop while a test is running.
  useEffect(() => {
    if (!testId) return undefined;
    const tick = async () => {
      try {
        const data = await getProviderShootout(testId);
        setTest(data?.test || null);
        if (data?.test?.status === "complete") {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch (err) {
        // Keep polling — transient errors are fine.
      }
    };
    pollRef.current = setInterval(tick, 4000);
    tick();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [testId]);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setFilePreview(URL.createObjectURL(f));
  };

  const submit = useCallback(async () => {
    if (!file && !sourceImageUrl) {
      toast.error("Pick a still image first");
      return;
    }
    setBusy(true);
    try {
      // If the user didn't upload a new file, fetch the scene still
      // from the public URL and re-submit it as multipart.
      let payloadFile = file;
      if (!payloadFile && sourceImageUrl) {
        const r = await fetch(sourceImageUrl);
        const blob = await r.blob();
        payloadFile = new File(
          [blob],
          "source.png",
          { type: blob.type || "image/png" },
        );
      }
      const data = await startProviderShootout({
        file: payloadFile,
        prompt,
      });
      setTestId(data?.test_id || "");
      setTest(null);
      toast.success("Shootout started — animating on every model in parallel");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || err?.message || "Couldn't start shootout",
      );
    } finally {
      setBusy(false);
    }
  }, [file, sourceImageUrl, prompt]);

  const rewriteDialogue = useCallback(async () => {
    const current = (prompt || "").trim();
    if (!current) {
      toast.error("Add a scene description first");
      return;
    }
    setRewriting(true);
    try {
      const data = await rewriteDialogueForPrompt(current);
      if (data?.prompt) {
        setPrompt(data.prompt.slice(0, 1000));
        toast.success("Added character dialogue + audio cues");
      } else {
        toast.error("Rewriter returned no output");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || err?.message || "Dialogue rewrite failed",
      );
    } finally {
      setRewriting(false);
    }
  }, [prompt]);

  if (!open) return null;

  const entries = test?.providers || [];
  const anyRunning = entries.some(
    (e) => e.status === "running" || e.status === "queued",
  );

  return (
    <div
      data-testid="shootout-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-zinc-950 border border-yellow-400/60 rounded-sm max-w-5xl w-full max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-zinc-950 border-b border-zinc-800 px-5 py-3 flex items-center justify-between gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-400 font-bold">
              // PROVIDER SHOOTOUT
            </div>
            <div className="font-display text-2xl font-black text-white">
              Pick the model that animates this scene best
            </div>
          </div>
          <button
            type="button"
            data-testid="shootout-close"
            onClick={onClose}
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-red-300 border border-zinc-800 hover:border-red-500/60 px-3 py-1.5 rounded-sm"
          >
            × close
          </button>
        </div>

        <div className="px-5 py-4 space-y-5">
          {/* ── Setup ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                Source still
              </div>
              <div className="aspect-video bg-zinc-900 border border-zinc-800 overflow-hidden rounded-sm flex items-center justify-center">
                {(filePreview || sourceImageUrl) ? (
                  <img
                    src={filePreview || sourceImageUrl}
                    alt="Source"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600">
                    no image selected
                  </div>
                )}
              </div>
              <label
                className={`font-mono text-[10px] uppercase tracking-[0.25em] cursor-pointer text-yellow-300 hover:text-yellow-200 border border-yellow-400/50 hover:border-yellow-400 px-3 py-2 rounded-sm inline-block ${
                  busy || testId ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                + Replace still
                <input
                  type="file"
                  accept="image/*"
                  data-testid="shootout-file-input"
                  onChange={handleFile}
                  disabled={busy || !!testId}
                  className="hidden"
                />
              </label>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <label
                  htmlFor="shootout-prompt"
                  className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500"
                >
                  Scene description (sent to every model)
                </label>
                <button
                  type="button"
                  data-testid="shootout-rewrite-dialogue"
                  onClick={rewriteDialogue}
                  disabled={rewriting || busy || !!testId || !(prompt || "").trim()}
                  title="Rewrite the prompt with character dialogue + audio cues so the models generate synced character voices instead of silent ambient video."
                  className="font-mono text-[10px] uppercase tracking-[0.25em] border border-cyan-400/60 text-cyan-200 hover:bg-cyan-400/10 disabled:opacity-50 disabled:cursor-not-allowed px-2 py-1 rounded-sm transition-colors"
                >
                  {rewriting ? "✎ writing…" : "✎ add dialogue"}
                </button>
              </div>
              <textarea
                id="shootout-prompt"
                data-testid="shootout-prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value.slice(0, 1000))}
                disabled={busy || !!testId}
                rows={8}
                placeholder="Describe what should happen in this scene…"
                className="w-full bg-black border border-zinc-800 focus:border-yellow-400/60 focus:outline-none rounded-sm px-2.5 py-1.5 text-sm text-zinc-100 leading-relaxed resize-y"
              />
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span>
                  {providers.length} models in parallel
                </span>
                {providers.map((p) => (
                  <span key={p.id} className="text-zinc-400">
                    {p.label.split(" ")[0]}{" "}
                    <span className="text-yellow-300">
                      ${(p.cost_per_clip_usd || 0).toFixed(2)}
                    </span>
                  </span>
                ))}
                {providers.length > 0 && (
                  <span className="text-zinc-300">
                    = total{" "}
                    <span className="text-yellow-300 font-bold">
                      ${providers
                        .reduce((acc, p) => acc + (p.cost_per_clip_usd || 0), 0)
                        .toFixed(2)}
                    </span>
                  </span>
                )}
              </div>
              {!testId && (
                <button
                  type="button"
                  data-testid="shootout-submit"
                  onClick={submit}
                  disabled={busy || providers.length === 0}
                  className={`w-full font-display text-sm font-black uppercase tracking-wide px-5 py-2.5 rounded-sm transition-colors ${
                    busy
                      ? "bg-zinc-800 text-zinc-500 cursor-not-allowed"
                      : "bg-yellow-400 text-black hover:bg-yellow-300"
                  }`}
                >
                  {busy
                    ? "▸ Starting…"
                    : `▶ Run shootout · ~$${providers
                        .reduce((acc, p) => acc + (p.cost_per_clip_usd || 0), 0)
                        .toFixed(2)} total`}
                </button>
              )}
            </div>
          </div>

          {/* ── Results grid ── */}
          {testId && (
            <div
              data-testid="shootout-results"
              className="grid grid-cols-1 md:grid-cols-3 gap-3"
            >
              {entries.map((e) => (
                <ResultCard
                  key={e.id}
                  entry={e}
                  costUsd={(providers.find((p) => p.id === e.id) || {}).cost_per_clip_usd || 0}
                  onPick={() => {
                    onPick?.(e.id);
                    toast.success(`Locked in: ${e.label}`);
                    onClose?.();
                  }}
                />
              ))}
            </div>
          )}

          {testId && !anyRunning && (
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-300 text-center pt-2">
              ● shootout complete — pick a winner above
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ResultCard({ entry, onPick, costUsd = 0 }) {
  const url = (raw) => {
    if (!raw) return "";
    if (raw.startsWith("http")) return raw;
    if (raw.startsWith("/")) return `${API_BASE}${raw}`;
    return raw;
  };
  const isReady = entry.status === "ready" && !!entry.video_url;
  const isFailed = entry.status === "failed";
  const isRunning = entry.status === "running";

  return (
    <div
      data-testid={`shootout-result-${entry.id}`}
      data-status={entry.status}
      className={`border rounded-sm overflow-hidden bg-black/40 flex flex-col ${
        isReady
          ? "border-emerald-400/60"
          : isFailed
            ? "border-red-500/60"
            : "border-zinc-800"
      }`}
    >
      <div className="aspect-video bg-zinc-950 relative">
        {isReady ? (
          <video
            src={url(entry.video_url)}
            controls
            playsInline
            preload="metadata"
            className="w-full h-full object-cover"
          />
        ) : isRunning ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
            <div className="w-8 h-8 border-2 border-yellow-400/30 border-t-yellow-400 rounded-full animate-spin" />
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-400">
              animating
            </div>
          </div>
        ) : isFailed ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 p-3 text-center">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-red-400">
              failed
            </div>
            <div className="text-[10px] text-zinc-400 line-clamp-3">
              {entry.error || "no output"}
            </div>
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              queued
            </div>
          </div>
        )}
      </div>
      <div className="p-3 space-y-2">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-300 line-clamp-2">
          {entry.label}
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 flex items-center justify-between gap-2">
          <span>{entry.duration_sec}s clip</span>
          {costUsd > 0 && (
            <span className="text-yellow-300">
              ~${costUsd.toFixed(2)}
            </span>
          )}
        </div>
        {isReady && (
          <button
            type="button"
            data-testid={`shootout-pick-${entry.id}`}
            onClick={onPick}
            className="w-full font-mono text-[10px] uppercase tracking-[0.25em] bg-emerald-400 text-black hover:bg-emerald-300 font-bold px-3 py-2 rounded-sm"
          >
            ✓ Use this model
          </button>
        )}
      </div>
    </div>
  );
}
